import SEOHead from '@/components/base/SEOHead';
import PageJsonLd from '@/components/base/PageJsonLd';
import { SITE_CONFIG } from '@/config';
import Layout from '@/components/feature/Layout';
import CTASection from '@/components/feature/CTASection';

/** 無料お試しページ（実仕様に一致: 承認制お試し・PJ1件・3名・期限なし）
 * フォーム申込は廃止→アプリの会社登録へ直接誘導（そのまま数分で使い始められる） */
export default function FreeTrial() {
  const signupUrl = SITE_CONFIG.signupUrl;
  return (
    <Layout>
      <SEOHead
        title="無料で今すぐお試し｜AIプロジェクト管理ツール ”おもいむすび”"
        description="”おもいむすび”を無料でお試しいただけます（プロジェクト1件・メンバー3名まで／期限なし）。AIタスク分解、AIガントチャート、AI日報、AIファイル管理など。クレジットカード不要・登録後すぐ利用開始。"
        keywords="プロジェクト管理ツール 無料トライアル,AI プロジェクト管理ツール 無料,プロジェクト管理 無料体験,AI ガントチャート 無料,プロジェクト管理 ツール 無料"
        canonical="https://omoimusubi.asia/free-trial/"
      />
      <PageJsonLd
        canonical="https://omoimusubi.asia/free-trial/"
        name="無料トライアル"
        description="”おもいむすび”を無料でお試しいただけます（プロジェクト1件・メンバー3名まで／期限なし）。AIタスク分解、AIガントチャート、AI日報、AIファイル管理など。クレジットカード不要・登録後すぐ利用開始。"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[800px] mx-auto px-4 md:px-6">
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 text-center mb-4">
            AIプロジェクト管理ツールを無料で今すぐお試し
          </h1>
          <p className="text-base text-foreground-600 text-center mb-3">
            会社を登録すると、その場ですぐ利用を開始できます。クレジットカードは不要です。
          </p>
          <p className="text-sm text-foreground-500 text-center mb-8">
            お試し利用はプロジェクト1件・メンバー3名まで（期限はありません）。「完全無料」「永久無料」ではなく、制限のない正式利用を開始すると月額料金が発生します。
          </p>

          {/* 申込フォームは廃止→アプリで直接登録（待ち時間ゼロ） */}
          <div className="text-center mb-12">
            <a
              href={signupUrl}
              className="inline-block bg-primary-500 hover:bg-primary-600 text-background-50 font-bold text-base px-10 py-4 rounded-lg transition-colors"
            >
              無料で今すぐお試し（会社を登録して開始）
            </a>
            <p className="text-xs text-foreground-500 mt-3">
              ログイン画面の「🏢 会社として新規登録（お試し）」から、会社名を入れるだけで始められます
            </p>
          </div>

          <div className="bg-background-100 rounded-lg border border-background-200/70 p-8 mb-10">
            <h2 className="text-lg font-bold text-foreground-900 mb-6 text-center">無料お試しで体験できること</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'AIタスク分解（資料アップロード）',
                'AIガントチャート作成',
                'AI日報による進捗自動更新',
                'AIファイル管理と質問応答',
                'チームチャット・コメント',
                '多言語UI切替（日英タイ）',
                '国別祝日カレンダー',
                '定例MTG・クリティカルパス表示',
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line text-green-600"></i>
                  {item}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-primary-50 rounded-lg p-6 border border-primary-200/70 mb-10">
            <h2 className="text-base font-bold text-foreground-900 mb-4">始め方（3分で完了）</h2>
            <ol className="space-y-3 text-sm text-foreground-700">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-primary-500 text-background-50 rounded-full flex items-center justify-center text-xs font-bold shrink-0">1</span>
                <span>「無料で今すぐお試し」ボタンからログイン画面を開き、「🏢 会社として新規登録（お試し）」を選びます。</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-primary-500 text-background-50 rounded-full flex items-center justify-center text-xs font-bold shrink-0">2</span>
                <span>会社名・お名前・メールアドレス・パスワードを入力すると、その場で利用開始できます。</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-primary-500 text-background-50 rounded-full flex items-center justify-center text-xs font-bold shrink-0">3</span>
                <span>プロジェクト資料をアップロードして、AIのタスク分解・ガントチャート自動生成を体験してください。</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-primary-500 text-background-50 rounded-full flex items-center justify-center text-xs font-bold shrink-0">4</span>
                <span>気に入ったら、アプリ内の「🚀 正式利用を申請する」から申請。承認後に制限が解除され、月額料金プランが開始します。</span>
              </li>
            </ol>
          </div>
        </div>
      </section>

      <CTASection
        title="導入のご相談もお気軽に"
        primaryLabel="デモ・導入相談を予約する"
        primaryTo="/contact/"
        secondaryLabel="機能を見る"
        secondaryTo="/features/"
      />
    </Layout>
  );
}
